import axios from "axios";
import { useCallback, useContext, useRef, useState } from "react";
import useSWR from "swr";
import {
  affiliateRequestID,
  assertAffiliateEnvironment,
  verifiedAffiliateEnvironment,
  isTestOnlyAffiliateOperation,
} from "./affiliateEnvironment";
import { AuthContext } from "../../../context/AuthContext";
import {
  AffiliateCommand,
  AffiliateCommandReceipt,
  AffiliateOperation,
  AffiliatePayoutPreview,
  AffiliateState,
  AffiliateLifecyclePreview,
} from "../../../interfaces/affiliate";
import {
  ADMIN_API_URL,
  isAffiliateEnvironment,
  isAffiliateTestEnvironment,
} from "../../../utils/env";

export const affiliateError = (error: unknown): string => {
  const value = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    value?.response?.data?.error ||
    value?.response?.data?.message ||
    value?.message ||
    "Request failed"
  );
};

export const useAffiliates = () => {
  const { tokenId, userEmail, queryConfig } = useContext(AuthContext);
  const allowed = isAffiliateEnvironment();
  const swr = useSWR<AffiliateState>(
    allowed && tokenId ? [ADMIN_API_URL("affiliates/state"), tokenId] : null,
    async () => {
      const { data } = await axios.get<AffiliateState>(
          ADMIN_API_URL("affiliates/state"),
          queryConfig()
        );
      assertAffiliateEnvironment(data);
      return data;
    },
    { refreshInterval: 5000, shouldRetryOnError: false }
  );
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [commandError, setCommandError] = useState<string>();
  const [receipt, setReceipt] = useState<AffiliateCommandReceipt>();
  const scope = `${process.env.REACT_APP_ENVIRONMENT}:${
    userEmail || tokenId || ""
  }`;
  const storageKey = userEmail
    ? `aevo:affiliate-admin:pending:${process.env.REACT_APP_ENVIRONMENT}:${userEmail}`
    : undefined;
  const [pending, setPending] = useState<{
    scope: string;
    command?: AffiliateCommand;
  }>();
  let saved: AffiliateCommand | undefined;
  try {
    if (storageKey)
      saved = JSON.parse(sessionStorage.getItem(storageKey) || "null");
  } catch {
    /* In-memory retry remains available. */
  }
  const retryCommand = pending?.scope === scope ? pending.command : saved;
  const setRetryCommand = useCallback(
    (command?: AffiliateCommand) => {
      setPending({ scope, command });
      try {
        if (storageKey) {
          if (command)
            sessionStorage.setItem(storageKey, JSON.stringify(command));
          else sessionStorage.removeItem(storageKey);
        }
      } catch {
        /* Keep the in-memory request. */
      }
    },
    [scope, storageKey]
  );

  const send = useCallback(
    async (command: AffiliateCommand) => {
      if (
        !allowed ||
        !tokenId ||
        swr.error ||
        !verifiedAffiliateEnvironment(swr.data)
      ) {
        throw new Error(
          "Affiliate controls require the verified backend for this environment and admin sign-in."
        );
      }
      if (
        isTestOnlyAffiliateOperation(command.operation) &&
        !isAffiliateTestEnvironment()
      )
        throw new Error(
          "This development command is available only on testnet."
        );
      if (busyRef.current)
        throw new Error("Wait for the current request to finish.");
      busyRef.current = true;
      setBusy(true);
      setCommandError(undefined);
      setReceipt(undefined);
      const readOnly = command.operation === "preview_payout" || command.operation === "preview_lifecycle";
      const wasUnconfirmed = retryCommand?.request_id === command.request_id;
      if (!readOnly) setRetryCommand(command);
      try {
        const response = await axios.post<
          | AffiliateCommandReceipt
          | AffiliatePayoutPreview
          | AffiliateLifecyclePreview
        >(ADMIN_API_URL("affiliates/command"), command, queryConfig());
        assertAffiliateEnvironment(response.data);
        if (command.operation === "preview_payout") {
          if (
            !("request" in response.data) ||
            !response.data.request?.affiliate_id
          ) {
            throw new Error("Backend did not return a payout preview.");
          }
        } else if (command.operation === "preview_lifecycle") {
          if (
            !("digest" in response.data) ||
            !/^[a-f0-9]{64}$/.test(response.data.digest)
          )
            throw new Error("Backend did not return a lifecycle preview.");
        } else if (
          !("success" in response.data) ||
          response.data.success !== true ||
          response.data.request_id !== command.request_id
        ) {
          throw new Error("Backend rejected the affiliate command.");
        } else {
          setReceipt(response.data);
        }
        setRetryCommand(undefined);
        // An accepted command is not an applied state change or a completed payment.
        await swr.mutate();
        return response.data;
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response
          ?.status;
        if (
          !readOnly && (wasUnconfirmed ||
          !status ||
          (status !== undefined && status >= 500))
        )
          setRetryCommand(command);
        else setRetryCommand(undefined);
        setCommandError(affiliateError(error));
        throw error;
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [allowed, tokenId, queryConfig, swr, retryCommand, setRetryCommand]
  );

  const command = useCallback(
    (operation: AffiliateOperation, payload: Record<string, unknown>) => {
      if (retryCommand)
        throw new Error(
          "Retry the unconfirmed request before submitting another change."
        );
      return send({
        request_id: affiliateRequestID(swr.data),
        operation,
        payload,
      });
    },
    [retryCommand, send, swr.data]
  );

  return {
    ...swr,
    data: swr.error ? undefined : swr.data,
    allowed,
    busy,
    commandError,
    receipt,
    retryCommand,
    command,
    retry: () => retryCommand && send(retryCommand),
  };
};
